import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react-native';
import { apiService } from '../services/apiService';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';
import { STALE_FEED_THRESHOLD_MS } from '../config/listingsConfig';

/**
 * useListingsFeed - Hook for fetching and subscribing to a model's listing feed.
 *
 * Fetches listings via the get-model-listings edge function on mount,
 * subscribes to Realtime updates on user_listing_feeds filtered by model_id,
 * and detects stale feeds (>24h) to trigger regeneration.
 *
 * Follows the pattern established by useRealtimeRecommendations.js.
 *
 * @param {string} modelId - The vehicle model ID to fetch listings for
 * @param {Object} session - The authenticated user session (from AuthContext)
 * @returns {{ listings: Array, isLoading: boolean, isEmpty: boolean, refetch: Function, feedGeneratedAt: string|null }}
 */
export const useListingsFeed = (modelId, session) => {
  const [listings, setListings] = useState([]);
  const [noLongerAvailableListings, setNoLongerAvailableListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [feedGeneratedAt, setFeedGeneratedAt] = useState(null);
  const channelRef = useRef(null);
  const isTriggeringFeedRef = useRef(false);

  // Stable user ID reference — prevents refetching on token refresh
  const userId = session?.user?.id;
  const sessionRef = useRef(session);
  sessionRef.current = session;

  /**
   * Fetch feed data from the get-model-listings edge function.
   * Returns the raw response for processing by the caller.
   */
  const fetchFeed = useCallback(async () => {
    if (!modelId || !userId) return null;

    try {
      const response = await apiService.getModelListings(modelId);
      return response;
    } catch (error) {
      logger.error('[useListingsFeed] Error fetching feed:', error);
      Sentry.captureException(error, {
        tags: { hook: 'useListingsFeed', operation: 'fetchFeed' },
        contexts: { feed: { model_id: modelId, user_id: userId } },
      });
      return null;
    }
  }, [modelId, userId]);

  /**
   * Process the API response and update state.
   */
  const processFeedResponse = useCallback(
    (response) => {
      if (!response) {
        setListings([]);
        setNoLongerAvailableListings([]);
        setIsLoading(false);
        return;
      }

      setListings(response.listings || []);
      setNoLongerAvailableListings(response.noLongerAvailableListings || []);
      setFeedGeneratedAt(response.feedGeneratedAt || null);
      setIsLoading(false);
    },
    []
  );

  /**
   * Check if the feed is stale (>24h) and trigger regeneration if needed.
   */
  const checkAndTriggerStaleFeed = useCallback(
    async (generatedAt) => {
      if (!generatedAt || isTriggeringFeedRef.current) return false;

      const feedAge = Date.now() - new Date(generatedAt).getTime();
      if (feedAge > STALE_FEED_THRESHOLD_MS) {
        logger.log(
          `[useListingsFeed] Feed is stale (${Math.round(feedAge / 3600000)}h old), triggering regeneration`
        );
        isTriggeringFeedRef.current = true;
        setIsLoading(true);

        try {
          await apiService.triggerListingFeed();
          // Feed generation is async — Realtime subscription will pick up the update
          // Keep isLoading=true until Realtime delivers new data
          return true;
        } catch (error) {
          logger.error('[useListingsFeed] Error triggering feed regeneration:', error);
          Sentry.captureException(error, {
            tags: { hook: 'useListingsFeed', operation: 'triggerStaleFeed' },
            contexts: { feed: { model_id: modelId, user_id: userId } },
          });
          setIsLoading(false);
          isTriggeringFeedRef.current = false;
          return false;
        }
      }

      return false;
    },
    [modelId, userId]
  );

  /**
   * Public refetch method — fetches fresh feed data.
   */
  const refetch = useCallback(async () => {
    setIsLoading(true);
    const response = await fetchFeed();
    processFeedResponse(response);
  }, [fetchFeed, processFeedResponse]);

  /**
   * Main effect: fetch data on mount and set up Realtime subscription.
   */
  useEffect(() => {
    if (!modelId || !userId) {
      setListings([]);
      setNoLongerAvailableListings([]);
      setIsLoading(false);
      setFeedGeneratedAt(null);
      return;
    }

    // Reset state for new model
    setIsLoading(true);
    setListings([]);
    setNoLongerAvailableListings([]);
    setFeedGeneratedAt(null);
    isTriggeringFeedRef.current = false;

    // Fetch initial data
    const fetchInitialData = async () => {
      const response = await fetchFeed();
      processFeedResponse(response);

      // Check for stale feed after initial load
      if (response?.feedGeneratedAt) {
        await checkAndTriggerStaleFeed(response.feedGeneratedAt);
      }
    };

    fetchInitialData();

    // Set up Realtime subscription on user_listing_feeds filtered by user_id
    // We filter client-side by model_id since Supabase Realtime only supports
    // filtering on a single column per subscription.
    const channelName = `listings_feed:${userId}:${modelId}`;

    // Clean up any existing channel with the same name
    const existingChannels = supabase.getChannels();
    const existingChannel = existingChannels.find(
      (ch) => ch.topic === `realtime:${channelName}`
    );
    if (existingChannel) {
      logger.log(`[useListingsFeed] Removing existing channel "${channelName}"`);
      supabase.removeChannel(existingChannel);
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_listing_feeds',
          filter: `user_id=eq.${userId}`,
        },
        async (payload) => {
          const record = payload.new || payload.old;

          // Only process events for the current model
          if (record?.model_id !== modelId) {
            return;
          }

          logger.log('[useListingsFeed] Realtime update received:', {
            eventType: payload.eventType,
            model_id: record?.model_id,
          });

          // On any relevant change, refetch the full feed to get consistent sorted data
          const response = await fetchFeed();
          if (response) {
            processFeedResponse(response);
            // Reset the triggering flag if feed was regenerated
            isTriggeringFeedRef.current = false;
          }
        }
      )
      .subscribe((status, err) => {
        if (err) {
          logger.error('[useListingsFeed] Realtime subscription error:', err);
          Sentry.captureException(err, {
            tags: { hook: 'useListingsFeed', operation: 'subscribe' },
            contexts: { feed: { model_id: modelId, user_id: userId } },
          });
          return;
        }

        if (status === 'SUBSCRIBED') {
          logger.log(
            `[useListingsFeed] Subscribed to Realtime for model: ${modelId}`
          );
        }
      });

    channelRef.current = channel;

    // Cleanup on unmount or dependency change
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [modelId, userId, fetchFeed, processFeedResponse, checkAndTriggerStaleFeed]);

  const isEmpty = !isLoading && listings.length === 0;

  /**
   * Update a single listing entry in-place (for optimistic UI updates).
   * @param {string} listingId
   * @param {Function} updater - receives the listing and returns the updated listing
   */
  const updateListing = useCallback((listingId, updater) => {
    setListings((prev) =>
      prev.map((l) => (l.id === listingId ? updater(l) : l))
    );
  }, []);

  return {
    listings,
    noLongerAvailableListings,
    isLoading,
    isEmpty,
    refetch,
    feedGeneratedAt,
    updateListing,
  };
};
